import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor for global error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle unauthorized errors (e.g., expired token)
    if (error.response?.status === 401) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      // Dispatch a custom event to notify AppContext to clear user state
      window.dispatchEvent(new Event('unauthorized'));
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

export const productApi = {
  getAll: (params) => api.get('/products', { params }),
  getOne: (slug) => api.get(`/products/${slug}`),
  getBySlug: (slug) => api.get(`/products/slug/${slug}`),
  getCategories: () => api.get('/categories'),
  getHeroImages: () => api.get('/hero-images'),
  getRecommendations: (productId) => api.get(`/products/${productId}/recommendations`),
  getPersonalRecommendations: () => api.get('/recommendations/personal'),
};

export const publicApi = {
  getSettings: () => api.get('/settings'),
  unsubscribe: (email) => api.get('/unsubscribe', { params: { email } }),
};

export const cartApi = {
  get: () => api.get('/cart'),
  addItem: (data) => api.post('/cart', data),
  updateItem: (productId, data) => api.put(`/cart/${productId}`, data),
  removeItem: (productId) => api.delete(`/cart/${productId}`),
  clear: () => api.delete('/cart'),
};

export const orderApi = {
  create: (data) => api.post('/orders', data),
  getAll: () => api.get('/orders'),
  getOne: (id) => api.get(`/orders/${id}`),
  verifyPayment: (data) => api.post('/orders/verify-payment', data),
};

export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getProducts: () => api.get('/admin/products'),
  getCategories: () => api.get('/admin/categories'),
  getOrders: () => api.get('/admin/orders'),
  getUsers: () => api.get('/admin/users'),
  updateSettings: (data) => api.put('/admin/settings', data),
  getSettings: () => api.get('/admin/settings'),
  getEncashmentRequests: (params) => api.get('/admin/encashment-requests', { params }),
  updateEncashmentStatus: (id, data) => api.put(`/admin/encashment-requests/${id}`, data),
};

export const chatApi = {
  createSession: () => api.post('/chat/sessions'),
  getSession: (id) => api.get(`/chat/sessions/${id}`),
  getMySession: () => api.get('/chat/sessions/me'),
  sendMessage: (sessionId, data) => api.post(`/chat/sessions/${sessionId}/messages`, data),
  getAllSessions: () => api.get('/chat/admin/sessions'),
  adminReply: (sessionId, data) => api.post(`/chat/admin/sessions/${sessionId}/reply`, data),
};

export const referralApi = {
  send: (email) => api.post('/referral/send', { email }),
  getStatus: () => api.get('/referral/status'),
  encash: () => api.post('/referral/encash'),
  getCredits: () => api.get('/referral/credits'),
};

export default api;
